import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    getThreadById,
    replyThread,
    closeThread,
    reopenThread,
} from "../../../api/serviceRecordApi";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { REACT_APP_API_URL } from "../../../env";

const ServiceRecordDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const isAdmin = user?.user?.LevelId === 1;
    const currentUserId = user?.employee?.id || user?.user?.customerId;
    const [thread, setThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [reply, setReply] = useState("");
    const [attachment, setAttachment] = useState(null);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const fetchThread = useCallback(async () => {
        const res = await getThreadById(id);
        setThread(res.data.thread);
        setMessages(res.data.messages);
    }, [id]);

    useEffect(() => {
        fetchThread();
    }, [fetchThread]);

    const handleReply = async () => {
        if (!reply) return;

        const formData = new FormData();
        formData.append("threadId", id);
        formData.append("message", reply);

        if (attachment) formData.append("attachment", attachment);

        await replyThread(formData);

        setReply("");
        setAttachment(null);
        fetchThread();
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "-";

        return new Date(dateString).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "UTC"
        });
    };

    const handleClose = async () => {
        await closeThread(id);
        fetchThread();
    };

    const handleReopen = async () => {
        await reopenThread(id);
        fetchThread();
    };

    if (!thread) return null;

    return (
        <div className="container-fluid admin-module-page admin-service-record-page py-4">
            <div className="admin-page-header d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                <div>
                    <span className="admin-page-kicker">Service Record</span>
                    <h3 className="admin-page-title fw-bold mb-1">Service Record Thread</h3>
                    <p className="admin-page-description mb-0">
                        View the conversation history, attachments, and status for this thread.
                    </p>
                </div>

                <button
                    className="btn btn-outline-secondary admin-page-action"
                    onClick={() => navigate("/service-record")}
                >
                    Back to List
                </button>
            </div>

            <div className="card admin-surface-card mb-4">
                <div className="card-body admin-surface-body">
                    <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
                        <div>
                            <h5 className="fw-semibold mb-1">
                                #{thread.ThreadID} - {thread.Subject}
                            </h5>
                            <div className="small text-muted">
                                <span className="me-3">
                                    <strong>Employee:</strong> {thread.EmployeeName}
                                </span>
                                <span className="me-3">
                                    <strong>Authorized By:</strong> {thread.AuthorizedBy}
                                </span>
                                <span>
                                    <strong>Created:</strong> {formatDateTime(thread.CreatedDate)}
                                </span>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {thread.Status === "Open" ? (
                                <span className="badge bg-success px-3 py-2">Open</span>
                            ) : (
                                <span className="badge bg-secondary px-3 py-2">Closed</span>
                            )}

                            {isAdmin && thread.Status === "Open" && (
                                <button
                                    className="btn btn-sm btn-danger"
                                    onClick={handleClose}
                                >
                                    Close Thread
                                </button>
                            )}

                            {isAdmin && thread.Status === "Closed" && (
                                <button
                                    className="btn btn-sm btn-warning"
                                    onClick={handleReopen}
                                >
                                    Reopen
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="card admin-surface-card mb-4">
                <div className="card-body p-0">
                    <div
                        className="p-3 p-lg-4"
                        style={{ maxHeight: "520px", overflowY: "auto" }}
                    >
                        {messages.map((msg) => {
                            const isMine = Number(msg.SenderID) === Number(currentUserId);

                            if (msg.IsSystemMessage) {
                                return (
                                    <div key={msg.MessageID} className="text-center my-3">
                                        <span className="badge bg-light text-dark">
                                            {msg.MessageText}
                                        </span>
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={msg.MessageID}
                                    className={`d-flex mb-3 ${isMine ? "justify-content-end" : "justify-content-start"}`}
                                >
                                    <div
                                        className={`rounded-4 px-3 py-2 shadow-sm ${Number(msg.SenderID) === Number(currentUserId)
                                            ? "bg-primary text-white"
                                            : "bg-light"
                                            }`}
                                        style={{ maxWidth: "72%" }}
                                    >
                                        <div className="fw-semibold small mb-1">
                                            {msg.SenderName === "Administrator" ? "Admin" : msg.SenderName}
                                        </div>

                                        <div className="small">
                                            {msg.MessageText}
                                        </div>

                                        {msg.FilePath && (
                                            <div className="small mt-1">
                                                <a
                                                    href={`${REACT_APP_API_URL}${msg.FilePath}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={`text-decoration-none ${Number(msg.SenderID) === Number(currentUserId) ? "text-red" : ""}`}
                                                >
                                                 📎Attachment
                                                </a>
                                            </div>
                                        )}

                                        <div className={`small mt-1 ${Number(msg.SenderID) === Number(currentUserId) ? "text-white-50" : "text-muted"}`}>
                                            {formatDateTime(msg.CreatedDate)}
                                        </div>
                                    </div>
                                    <div ref={messagesEndRef} />
                                </div>
                            );
                        })}
                    </div>
                </div>

                {thread.Status === "Open" && (
                    <div className="card-footer bg-light p-3 p-lg-4">
                        <div className="row g-3 align-items-end">
                            <div className="col-12">
                                <label className="form-label fw-semibold">Reply</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    placeholder="Write your reply..."
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                />
                            </div>

                            <div className="col-12 col-lg-7">
                                <label className="form-label fw-semibold">Attachment</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    onChange={(e) => setAttachment(e.target.files[0])}
                                />
                            </div>

                            <div className="col-12 col-lg-5 d-flex justify-content-lg-end">
                                <button
                                    className="btn btn-primary admin-page-action"
                                    onClick={handleReply}
                                    disabled={!reply.trim()}
                                >
                                    Send Reply
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ServiceRecordDetail;
